<template>
  <FullScreenLayoutContent>
    <template #header>
      <div class="flex justify-end px-4 py-2 lg:px-6">
        <BaseTextButton
          color="text"
          size="M"
          trailing-icon="arrow_right_alt"
          @click="finishCoachOnboarding"
          >{{ $t('common.skip') }}</BaseTextButton
        >
      </div>
    </template>
    <div class="flex grow flex-col justify-center p-6">
      <h3 class="text-headline-medium lg:text-display-medium text-gray-700">
        {{ $t('coaching.onboarding.title') }}
      </h3>
      <p class="text-body-large">
        {{ $t('coaching.onboarding.description') }}
      </p>
      <p class="text-body-large mt-8 mb-2">{{ $t('coaching.onboarding.instruction') }}</p>

      <ul class="flex flex-col gap-3">
        <li>
          <OnboardingStep
            :step="1"
            :is-active="step === 'members'"
            :is-completed="step !== 'members'"
            :title="$t('coaching.onboarding.steps.teamMembers.title')"
            :description="$t('coaching.onboarding.steps.teamMembers.description')"
          >
            <BaseTextButton v-if="step !== 'members'" @click="step = 'members'">{{
              $t('common.edit')
            }}</BaseTextButton>
            <template #bottom>
              <LoadingWrapper :state="coacheesStatus">
                <ul class="flex flex-col gap-0.5">
                  <li
                    v-for="member in coacheesList.map((coachee) => coachee.coachee)"
                    :key="member.id"
                    class="bg-surface-container rounded-small px-6 py-2"
                  >
                    <div class="flex flex-row items-center">
                      <BaseMemberAvatar :member="member" class="mr-4" />
                      <div class="flex grow flex-col">
                        <span class="text-body-large font-medium">{{
                          memberFullName(member)
                        }}</span>
                        <span
                          v-if="member.email"
                          class="text-body-medium text-on-surface-variant"
                          >{{ member.email }}</span
                        >
                      </div>
                      <BaseIconButton
                        icon="remove"
                        color="outlined"
                        size="XS"
                        @click="deleteCoaching(member.id)"
                      />
                    </div>
                  </li>
                </ul>
                <div class="flex w-full flex-row gap-2 px-6 py-4">
                  <BaseTextButton
                    color="tonalPrimary"
                    size="M"
                    class="grow"
                    @click="openAddCoacheeDialog"
                    icon="add"
                  >
                    {{ $t('coaching.addCoachees') }}
                  </BaseTextButton>
                  <BaseTextButton
                    color="filled"
                    size="M"
                    :disabled="!areTopicsReady"
                    trailing-icon="arrow_forward"
                    @click="isMembersLastStep ? finishCoachOnboarding() : nextStep()"
                  >
                    {{ isMembersLastStep ? $t('common.finish') : $t('common.next') }}
                  </BaseTextButton>
                </div>
              </LoadingWrapper>
            </template>
          </OnboardingStep>
        </li>
        <li v-if="hasJournalTopics">
          <OnboardingStep
            :step="2"
            :is-active="step === 'surveys'"
            :is-completed="step === 'objectives'"
            :title="$t('coaching.onboarding.steps.teamSurveys.title')"
            :description="$t('coaching.onboarding.steps.teamSurveys.description')"
          >
            <BaseTextButton v-if="step === 'objectives'" @click="step = 'surveys'">{{
              $t('common.edit')
            }}</BaseTextButton>
            <template #bottom>
              <LoadingWrapper :state="surveysStatus">
                <ul v-if="surveys.length" class="mt-0.5 flex flex-col gap-0.5">
                  <li
                    v-for="survey in surveys"
                    :key="survey.id"
                    class="last:[&>div]:rounded-b-large"
                  >
                    <div
                      class="rounded-extra-small flex-rounded bg-surface-container group relative flex grow items-center gap-4 overflow-hidden px-4 py-2"
                    >
                      <div class="flex grow flex-row items-center">
                        <div
                          class="bg-primary-container text-on-primary-container mr-4.5 ml-1 flex h-10 w-10 items-center justify-center rounded-full"
                        >
                          <BaseIcon size="24">{{ survey.feedbackTopic.icon }}</BaseIcon>
                        </div>
                        <div class="flex flex-col">
                          <h3 class="text-title-medium">
                            {{ survey.title ?? survey.feedbackTopic.title }}
                          </h3>
                          <span class="-mt-0.5">
                            <CoachSurveyRecurringInformation
                              class="text-on-surface-variant"
                              :recurrence="survey.recurrence"
                              :next-occurrence-at="survey.nextOccurrenceAt"
                              :createdAt="survey.createdAt"
                              :numberOfSurveys="survey.numberOfSurveys"
                              :showNextOccurence="false"
                              :showCreatedAt="false"
                            />
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
                <div class="flex w-full flex-row gap-2 px-6 py-4">
                  <BaseTextButton
                    color="tonalPrimary"
                    size="M"
                    class="grow"
                    @click="openStartSurveyDialog"
                    icon="add"
                  >
                    {{ $t('common.startCoachSurvey') }}
                  </BaseTextButton>
                  <BaseTextButton
                    color="filled"
                    size="M"
                    trailing-icon="arrow_forward"
                    @click="isSurveysLastStep ? finishCoachOnboarding() : nextStep()"
                  >
                    {{ isSurveysLastStep ? $t('common.finish') : $t('common.next') }}
                  </BaseTextButton>
                </div>
              </LoadingWrapper>
            </template>
          </OnboardingStep>
        </li>
        <li v-if="hasEvaluationTopics">
          <OnboardingStep
            v-if="isAssessmentModuleActive"
            :step="objectivesStepNumber"
            :is-active="step === 'objectives'"
            :title="$t('coaching.onboarding.steps.teamObjectives.title')"
            :description="$t('coaching.onboarding.steps.teamObjectives.description')"
          >
            <template #bottom>
              <LoadingWrapper :state="assessmentsStatus">
                <ul v-if="assessments.length" class="mt-0.5 flex flex-col gap-0.5">
                  <li
                    v-for="assessment in assessments"
                    :key="assessment.id"
                    class="last:[&>div]:rounded-b-large"
                  >
                    <div
                      class="rounded-extra-small flex-rounded bg-surface-container group relative flex grow cursor-pointer items-center gap-4 overflow-hidden px-4 py-2"
                    >
                      <div class="flex grow flex-row items-center">
                        <div
                          class="bg-primary-container text-on-primary-container mr-4.5 ml-1 flex h-10 w-10 items-center justify-center rounded-full"
                        >
                          <BaseIcon size="24">{{ assessment.feedbackTopic.icon }}</BaseIcon>
                        </div>
                        <div class="flex flex-col">
                          <h3 class="text-title-medium">
                            {{ assessment.title ?? assessment.feedbackTopic.title }}
                          </h3>
                          <span class="-mt-0.5"> </span>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
                <div class="flex w-full flex-row gap-2 px-6 py-4">
                  <BaseTextButton
                    color="tonalPrimary"
                    size="M"
                    class="grow"
                    @click="openStartAssessmentDialog"
                    icon="add"
                  >
                    {{ $t('common.startAssessment') }}
                  </BaseTextButton>

                  <BaseTextButton
                    color="filled"
                    @click="finishCoachOnboarding"
                    size="M"
                    trailing-icon="arrow_forward"
                    >{{ $t('common.finish') }}</BaseTextButton
                  >
                </div>
              </LoadingWrapper>
            </template>
          </OnboardingStep>
          <OnboardingStep
            v-else
            :step="objectivesStepNumber"
            :is-active="step === 'objectives'"
            :title="$t('coaching.onboarding.steps.teamObjectives.title')"
            :description="$t('coaching.onboarding.steps.teamObjectives.description')"
          >
            <template #bottom>
              <LoadingWrapper :state="feedbackGoalsStatus">
                <ul v-if="feedbackGoalsList.length" class="mt-0.5 flex flex-col gap-0.5">
                  <li
                    v-for="feedbackGoal in feedbackGoalsList"
                    :key="feedbackGoal.id"
                    class="last:[&>div]:rounded-b-large"
                  >
                    <div
                      class="rounded-extra-small flex-rounded bg-surface-container group relative flex grow cursor-pointer items-center gap-4 overflow-hidden px-4 py-2"
                    >
                      <div class="flex grow flex-row items-center">
                        <div
                          class="bg-primary-container text-on-primary-container mr-4.5 ml-1 flex h-10 w-10 items-center justify-center rounded-full"
                        >
                          <BaseIcon size="24">{{ feedbackGoal.feedbackTopic.icon }}</BaseIcon>
                        </div>
                        <div class="flex flex-col">
                          <h3 class="text-title-medium">
                            {{ feedbackGoal.feedbackTopic.title }}
                          </h3>
                          <span class="-mt-0.5"> </span>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
                <div class="flex w-full flex-row gap-2 px-6 py-4">
                  <BaseTextButton
                    color="tonalPrimary"
                    size="M"
                    class="grow"
                    @click="openCreateFeedbackGoalDialog"
                    icon="add"
                  >
                    {{ $t('common.createFeedbackGoal') }}
                  </BaseTextButton>

                  <BaseTextButton
                    color="filled"
                    @click="finishCoachOnboarding"
                    size="M"
                    trailing-icon="arrow_forward"
                    >{{ $t('common.finish') }}</BaseTextButton
                  >
                </div>
              </LoadingWrapper>
            </template>
          </OnboardingStep>
        </li>
      </ul>
      <AddCoacheesDialog
        :is-open="isOpenAddCoacheeDialog"
        :existing-coachees="coacheesList"
        @close="closeAddCoacheeDialog"
        @add-coachees="addCoachees"
      />
      <StartSurveyDialog
        :is-open="isOpenStartSurveyDialog"
        @close-dialog="closeStartSurveyDialog"
        @survey-created="() => closeStartSurveyDialog()"
      />
      <CreateCoachFeedbackGoalDialog
        :is-open="isOpenCreateFeedbackGoalDialog"
        @close-dialog="closeCreateFeedbackGoalDialog"
        @goal-created="() => closeCreateFeedbackGoalDialog()"
      />
      <StartAssessmentDialog
        v-if="isAssessmentModuleActive"
        :is-open="isOpenStartAssessmentDialog"
        @close-dialog="closeStartAssessmentDialog"
        @assessment-created="() => closeStartAssessmentDialog()"
      />
    </div>
  </FullScreenLayoutContent>
</template>

<script setup lang="ts">
  import { computed, onBeforeMount, onMounted, onUnmounted, ref } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useEventBus } from '@vueuse/core'
  import { useRouter } from 'vue-router'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import FullScreenLayoutContent from '@/core/components/layouts/FullScreenLayoutContent.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import { EventKey } from '@/core/events/event-key'
  import { useMemeberSettings } from '@/core/composables/use-member-settings'
  import { ApiRequestStatus } from '@/core/models/api/api-request-status'
  import { useSnackStore } from '@/stores/snack.store'
  import { useUserStore } from '@/stores/user.store'
  import { useAllCoachees } from '@/modules/coachees/composables/useAllCoachees'
  import StartSurveyDialog from '@/modules/start-survey/components/StartSurveyDialog.vue'
  import { useCoachSurveys } from '@/modules/coach-surveys/composables/use-coach-surveys'
  import CreateCoachFeedbackGoalDialog from '@/modules/coach-feedback-goals/components/CreateCoachFeedbackGoalDialog.vue'
  import { useCoachFeedbackGoals } from '@/modules/coach-feedback-goals/composables/use-coach-feedback-goals'
  import { useAssessments } from '@/modules/assessment/components/use-assessments'
  import StartAssessmentDialog from '@/modules/start-assessment/components/StartAssessmentDialog.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import CoachSurveyRecurringInformation from '@/modules/coach-surveys/components/CoachSurveyRecurringInformation.vue'
  import { useI18n } from 'vue-i18n'
  import { useApplicationModule } from '@/core/composables/use-application-module'
  import { ApplicationModule } from '@/core/models/organization'
  import AddCoacheesDialog from '../components/AddCoacheesDialog.vue'
  import { useMutateCoachees } from '../composables/useMutateCoachees'
  import OnboardingStep from '@/modules/onboarding/components/OnboardingStep.vue'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import { memberFullName } from '@/core/models/member/member-name'
  import { useFeedbackTopicStore } from '@/stores/feedback-topic.store'

  type OnboardingStepId = 'members' | 'surveys' | 'objectives'

  const router = useRouter()
  const { t } = useI18n()
  const { user } = useUserStore()
  const { showError } = useSnackStore()
  const { updateMemberSettings, updateStatus } = useMemeberSettings()
  const { allCoachees, status: coacheesStatus } = useAllCoachees()
  const { addCoachees, deleteCoaching } = useMutateCoachees()
  const { data: surveysData, status: surveysStatus, reload: reloadSurveys } = useCoachSurveys()
  const {
    feedbackGoals,
    status: feedbackGoalsStatus,
    reload: reloadFeedbackGoals,
  } = useCoachFeedbackGoals()
  const {
    data: assessmentsData,
    status: assessmentsStatus,
    reload: reloadAssessments,
  } = useAssessments()
  const { hasModule } = useApplicationModule()
  const feedbackTopicStore = useFeedbackTopicStore()
  const {
    journalTemplates,
    evaluationTemplates,
    loadingStatus: topicsLoadingStatus,
  } = storeToRefs(feedbackTopicStore)
  const { loadTemplates } = feedbackTopicStore

  const coacheesList = computed(() => allCoachees.value ?? [])
  const surveys = computed(() => surveysData.value ?? [])
  const feedbackGoalsList = computed(() => feedbackGoals.value ?? [])
  const assessments = computed(() => assessmentsData.value ?? [])
  const isAssessmentModuleActive = computed(() => hasModule(ApplicationModule.ASSESSMENT))
  const hasJournalTopics = computed(() => journalTemplates.value.length > 0)
  const hasEvaluationTopics = computed(() => evaluationTemplates.value.length > 0)
  const areTopicsReady = computed(
    () =>
      topicsLoadingStatus.value === ApiRequestStatus.Success ||
      topicsLoadingStatus.value === ApiRequestStatus.Error,
  )
  const isMembersLastStep = computed(
    () => areTopicsReady.value && !hasJournalTopics.value && !hasEvaluationTopics.value,
  )
  const isSurveysLastStep = computed(() => !hasEvaluationTopics.value)
  const objectivesStepNumber = computed(() => (hasJournalTopics.value ? 3 : 2))

  const {
    isOpen: isOpenAddCoacheeDialog,
    openDialog: openAddCoacheeDialog,
    closeDialog: closeAddCoacheeDialog,
  } = useDialog()

  const {
    isOpen: isOpenStartSurveyDialog,
    openDialog: openStartSurveyDialog,
    closeDialog: closeStartSurveyDialog,
  } = useDialog()

  const {
    isOpen: isOpenCreateFeedbackGoalDialog,
    openDialog: openCreateFeedbackGoalDialog,
    closeDialog: closeCreateFeedbackGoalDialog,
  } = useDialog()
  const {
    isOpen: isOpenStartAssessmentDialog,
    openDialog: openStartAssessmentDialog,
    closeDialog: closeStartAssessmentDialog,
  } = useDialog()

  const feedbackGoalsUpdatedBus = useEventBus(EventKey.coachFeedbackGoalsUpdated)
  let stopFeedbackGoalsUpdateBus: (() => void) | undefined
  const surveysUpdatedBus = useEventBus(EventKey.surveyUpdated)
  let stopSurveysUpdateBus: (() => void) | undefined
  const assessmentCreatedBus = useEventBus(EventKey.assessmentCreated)
  let stopAssessmentCreatedBus: (() => void) | undefined

  onMounted(() => {
    loadTemplates()
    stopFeedbackGoalsUpdateBus = feedbackGoalsUpdatedBus.on(() => {
      reloadFeedbackGoals()
    })
    stopSurveysUpdateBus = surveysUpdatedBus.on(() => {
      reloadSurveys()
    })
    stopAssessmentCreatedBus = assessmentCreatedBus.on(() => {
      reloadAssessments()
    })
  })

  onUnmounted(() => {
    stopFeedbackGoalsUpdateBus?.()
    stopSurveysUpdateBus?.()
    stopAssessmentCreatedBus?.()
  })

  onBeforeMount(() => {
    if (user.settings.coachOnboardingCompleted) {
      router.replace({ name: 'coachees' })
    }
  })

  const completeCoachOnboarding = async () => {
    await updateMemberSettings({
      coachOnboardingCompleted: true,
    })

    if (updateStatus.value !== ApiRequestStatus.Success) {
      showError(t('coaching.onboarding.updateError'))
      return false
    }

    return true
  }

  const finishCoachOnboarding = async () => {
    const isCompleted = await completeCoachOnboarding()
    if (isCompleted) {
      router.push({ name: 'coachees' })
    }
  }

  const step = ref<OnboardingStepId>('members')

  const nextStep = () => {
    if (step.value === 'members') {
      if (hasJournalTopics.value) {
        step.value = 'surveys'
      } else if (hasEvaluationTopics.value) {
        step.value = 'objectives'
      }
      return
    }

    if (step.value === 'surveys' && hasEvaluationTopics.value) {
      step.value = 'objectives'
    }
  }
</script>
